import { useEffect, useRef, useState } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";
import {
  Upload, Download, RefreshCcw, FileText,
  CheckCircle2, Database, Zap, Cloud,
} from "lucide-react";

// Sync log timestamps are stored in UTC — always display them in Brisbane
// time explicitly, so this is correct regardless of the viewing device's
// own local timezone (not just when staff happen to be physically in QLD).
function formatBrisbaneTime(isoString) {
  if (!isoString) return "";
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return isoString;
  const parts = new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Brisbane",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  }).formatToParts(d).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
}

export default function AdminSync() {
  const [logs,        setLogs]       = useState([]);
  const [lastResult,  setLastResult]  = useState(null);
  const [adapterMode, setAdapterMode] = useState("csv");
  const [pulling,     setPulling]     = useState(false);
  const [pullResult,  setPullResult]  = useState(null);
  const fileInput       = useRef(null);
  const statusFileInput = useRef(null);

  const loadLogs = async () => {
    try {
      const { data } = await adminApi.get("/admin/sync/logs");
      setLogs(data.items || []);
    } catch { /* ignore */ }
  };

  const loadMode = async () => {
    try {
      const { data } = await adminApi.get("/admin/settings");
      setAdapterMode(data.adapter_mode || "csv");
    } catch { /* ignore */ }
  };

  useEffect(() => { loadLogs(); loadMode(); }, []);

  // ── ALL CSV SYNC LOGIC UNTOUCHED ─────────────────────────────────
  const uploadProducts = async (file) => {
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await adminApi.post("/admin/sync/products/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLastResult(data);
      toast.success(`Sync complete · +${data.inserted} / ~${data.updated}`);
      loadLogs();
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
  };

  const uploadStatuses = async (file) => {
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await adminApi.post("/admin/sync/orders/status-update", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Updated ${data.applied} orders`);
      loadLogs();
    } catch (e) { toast.error(e.response?.data?.detail || "Status update failed"); }
  };

  const downloadSample = async () => {
    try {
      const res = await adminApi.get("/admin/sync/sample-csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "pinnacle_sample.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download sample"); }
  };

  const downloadOrders = () => {
    adminApi.get("/admin/sync/orders/export", { responseType: "blob" }).then(res => {
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "bcp_orders.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      loadLogs(); toast.success("Orders exported");
    }).catch(() => toast.error("Export failed"));
  };
  // ── END CSV LOGIC ────────────────────────────────────────────────

  // ── API MODE: Pull inventory from Pinnacle ────────────────────────
  const pullFromPinnacle = async () => {
    if (!window.confirm(
      `Pull all inventory from Pinnacle OPS API?\n\n` +
      `This will import all parts into BCP.\n` +
      `37,000+ parts may take 3–5 minutes.\n\n` +
      `Existing parts will be updated, new parts added.`
    )) return;

    setPulling(true);
    setPullResult(null);
    toast.info("Pulling inventory from Pinnacle… please wait");

    try {
      const { data } = await adminApi.post("/admin/pinnacle/pull-inventory");
      setPullResult(data);
      toast.success(
        `✅ Done! ${data.inserted} new · ${data.updated} updated · ${data.skipped} skipped`
      );
      loadLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Pull failed — check sync logs");
    } finally {
      setPulling(false);
    }
  };

  const isApiMode = adapterMode === "api";

  return (
    <div className="space-y-5" data-testid="admin-sync-page">

      {/* Header */}
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-1"
          style={{ color:"#94a3b8" }}>Integration</div>
        <h1 className="font-heading text-[28px] font-bold" style={{ color:"#0f172a" }}>
          Pinnacle Sync Engine
        </h1>
        <p className="text-[12px] mt-0.5" style={{ color:"#64748b" }}>
          {isApiMode
            ? "Live API mode — inventory pulls directly from Pinnacle OPS API"
            : "CSV mode — import product data from Pinnacle CSV export"}
        </p>
      </div>

      {/* Mode banner */}
      <div className="flex items-center gap-3 p-4 rounded-2xl"
        style={{
          background: isApiMode ? "#dcfce7" : "#fffbeb",
          border: `1px solid ${isApiMode ? "#86efac" : "#fde68a"}`,
        }}>
        {isApiMode
          ? <Cloud className="h-5 w-5 shrink-0" style={{ color:"#16a34a" }}/>
          : <Database className="h-5 w-5 shrink-0" style={{ color:"#d97706" }}/>
        }
        <div>
          <div className="font-heading font-bold text-[14px] uppercase"
            style={{ color: isApiMode ? "#15803d" : "#92400e" }}>
            {isApiMode ? "Live API Mode Active" : "CSV / Offline Mode"}
          </div>
          <div className="text-[12px]" style={{ color: isApiMode ? "#16a34a" : "#b45309" }}>
            {isApiMode
              ? "Both Pinnacle APIs connected. Use the button below to pull inventory."
              : "Switch to API mode in Settings → Pinnacle to enable live sync."}
          </div>
        </div>
      </div>

      {/* ── API MODE: Pull inventory ── */}
      {isApiMode && (
        <div className="bg-white rounded-2xl p-5 shadow-sm space-y-4"
          style={{ border:"1px solid #dde3ef", borderTop:"3px solid #16a34a" }}>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1"
              style={{ color:"#94a3b8" }}>Live API</div>
            <div className="font-heading font-bold text-[16px] uppercase mb-1"
              style={{ color:"#0f172a" }}>
              Pull Inventory from Pinnacle
            </div>
            <p className="text-[12px]" style={{ color:"#64748b" }}>
              Pulls all 37,000+ parts directly from Pinnacle OPS API.
              New parts are added. Existing parts are updated. Takes 3–5 minutes.
            </p>
          </div>

          <button onClick={pullFromPinnacle} disabled={pulling}
            className="flex items-center gap-2 h-11 px-6 rounded-xl text-white text-[13px] font-bold uppercase tracking-wide disabled:opacity-50"
            style={{ background: pulling ? "#94a3b8" : "#16a34a" }}
            data-testid="pinnacle-pull-btn">
            {pulling
              ? <><RefreshCcw className="h-4 w-4 animate-spin"/> Pulling inventory…</>
              : <><Zap className="h-4 w-4"/> Pull Inventory from Pinnacle</>
            }
          </button>

          {pulling && (
            <div className="text-[12px] font-mono animate-pulse" style={{ color:"#64748b" }}>
              Fetching pages from Pinnacle OPS API… this may take a few minutes for 37,000+ parts
            </div>
          )}

          {pullResult && (
            <div className="p-4 rounded-xl space-y-2"
              style={{ background:"#f0fdf4", border:"1px solid #86efac" }}>
              <div className="flex items-center gap-2 font-heading font-bold text-[14px] uppercase"
                style={{ color:"#15803d" }}>
                <CheckCircle2 className="h-5 w-5"/> Inventory sync complete
              </div>
              <div className="grid grid-cols-3 gap-3 mt-2">
                {[
                  { label:"New parts",     value: pullResult.inserted, color:"#16a34a" },
                  { label:"Updated",       value: pullResult.updated,  color:"#1e5bb8" },
                  { label:"Skipped",       value: pullResult.skipped,  color:"#94a3b8" },
                ].map(({ label, value, color }) => (
                  <div key={label} className="text-center p-3 bg-white rounded-xl"
                    style={{ border:"1px solid #dde3ef" }}>
                    <div className="font-heading text-[24px] font-bold" style={{ color }}>
                      {value.toLocaleString()}
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-widest mt-1"
                      style={{ color:"#94a3b8" }}>
                      {label}
                    </div>
                  </div>
                ))}
              </div>
              {pullResult.warnings?.length > 0 && (
                <div className="mt-2 text-[11px]" style={{ color:"#d97706" }}>
                  {pullResult.warnings.length} warnings — check sync log below
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── CSV MODE: 3-step cards ── */}
      {!isApiMode && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Step 1 — Import products */}
          <div className="bg-white rounded-2xl p-5 shadow-sm flex flex-col"
            style={{ border:"1px solid #dde3ef", borderTop:"3px solid #e31e24" }}>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1"
              style={{ color:"#94a3b8" }}>Step 1</div>
            <div className="font-heading font-bold text-[16px] uppercase mb-2"
              style={{ color:"#0f172a" }}>Products · Import</div>
            <p className="text-[12px] flex-1" style={{ color:"#64748b" }}>
              Upload a Pinnacle export CSV. Existing SKUs are updated; new SKUs are created.
            </p>
            <div className="mt-4 flex gap-2 flex-wrap">
              <input type="file" accept=".csv,text/csv" ref={fileInput} hidden
                onChange={e => e.target.files?.[0] && uploadProducts(e.target.files[0])}
                data-testid="admin-sync-file-input"/>
              <button onClick={() => fileInput.current?.click()}
                className="flex items-center gap-1.5 h-9 px-4 rounded-xl text-[12px] font-bold uppercase tracking-wide text-white"
                style={{ background:"#e31e24" }}
                data-testid="admin-sync-upload-btn">
                <Upload className="h-3.5 w-3.5"/> Upload CSV
              </button>
              <button onClick={downloadSample}
                className="flex items-center gap-1.5 h-9 px-3 rounded-xl text-[12px] font-semibold"
                style={{ background:"#f5f7fb", border:"1px solid #dde3ef", color:"#475569" }}
                data-testid="admin-sync-sample-btn">
                <FileText className="h-3.5 w-3.5"/> Sample
              </button>
            </div>
          </div>

          {/* Step 2 — Export orders */}
          <div className="bg-white rounded-2xl p-5 shadow-sm flex flex-col"
            style={{ border:"1px solid #dde3ef", borderTop:"3px solid #1e5bb8" }}>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1"
              style={{ color:"#94a3b8" }}>Step 2</div>
            <div className="font-heading font-bold text-[16px] uppercase mb-2"
              style={{ color:"#0f172a" }}>Orders · Export</div>
            <p className="text-[12px] flex-1" style={{ color:"#64748b" }}>
              Download paid/COD orders in Pinnacle-ready CSV. Auto-marked exported after download.
            </p>
            <button onClick={downloadOrders}
              className="mt-4 flex items-center gap-1.5 h-9 px-4 rounded-xl text-[12px] font-bold uppercase tracking-wide text-white"
              style={{ background:"#1e5bb8" }}
              data-testid="admin-sync-export-btn">
              <Download className="h-3.5 w-3.5"/> Download orders CSV
            </button>
          </div>

          {/* Step 3 — Status update */}
          <div className="bg-white rounded-2xl p-5 shadow-sm flex flex-col"
            style={{ border:"1px solid #dde3ef", borderTop:"3px solid #16a34a" }}>
            <div className="text-[10px] font-mono uppercase tracking-widest mb-1"
              style={{ color:"#94a3b8" }}>Step 3</div>
            <div className="font-heading font-bold text-[16px] uppercase mb-2"
              style={{ color:"#0f172a" }}>Status · Update</div>
            <p className="text-[12px] flex-1" style={{ color:"#64748b" }}>
              Re-upload a status CSV from Pinnacle to sync back order progress.
            </p>
            <div className="mt-4">
              <input type="file" accept=".csv,text/csv" ref={statusFileInput} hidden
                onChange={e => e.target.files?.[0] && uploadStatuses(e.target.files[0])}
                data-testid="admin-sync-status-input"/>
              <button onClick={() => statusFileInput.current?.click()}
                className="flex items-center gap-1.5 h-9 px-4 rounded-xl text-[12px] font-semibold"
                style={{ background:"#f5f7fb", border:"1px solid #dde3ef", color:"#475569" }}
                data-testid="admin-sync-status-btn">
                <RefreshCcw className="h-3.5 w-3.5"/> Upload status CSV
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Last CSV result */}
      {!isApiMode && lastResult && (
        <div className="bg-white rounded-2xl p-4 shadow-sm flex items-start gap-3"
          style={{ border:"1px solid #dde3ef" }}>
          <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0" style={{ color:"#16a34a" }}/>
          <div>
            <div className="font-semibold text-[13px] mb-0.5" style={{ color:"#0f172a" }}>
              Last import result
            </div>
            <div className="text-[12px]" style={{ color:"#64748b" }}>
              Inserted <b style={{ color:"#16a34a" }}>{lastResult.inserted}</b>
              {" · "}Updated <b style={{ color:"#1e5bb8" }}>{lastResult.updated}</b>
              {" · "}Skipped <b>{lastResult.skipped}</b>
            </div>
            {lastResult.warnings?.length > 0 && (
              <ul className="mt-2 text-[11px] list-disc pl-4 space-y-0.5"
                style={{ color:"#d97706" }}>
                {lastResult.warnings.slice(0, 5).map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Sync log terminal */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden"
        style={{ border:"1px solid #dde3ef" }} data-testid="admin-sync-logs">
        <div className="px-4 py-3 flex items-center justify-between"
          style={{ borderBottom:"1px solid #dde3ef" }}>
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4" style={{ color:"#475569" }}/>
            <span className="font-heading font-bold uppercase tracking-wide text-[13px]"
              style={{ color:"#0f172a" }}>Sync log</span>
          </div>
          <button onClick={loadLogs}
            className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-[11px] font-semibold"
            style={{ background:"#f5f7fb", border:"1px solid #dde3ef", color:"#475569" }}>
            <RefreshCcw className="h-3 w-3"/> Refresh
          </button>
        </div>
        <div className="p-4 max-h-96 overflow-y-auto font-mono text-[12px]"
          style={{ background:"#0d1117", color:"#4ade80" }}>
          {logs.length === 0
            ? <div style={{ color:"#475569" }}>[ no sync events recorded ]</div>
            : logs.map(l => (
              <div key={l.id} className="mb-3 pb-3"
                style={{ borderBottom:"1px solid #1a2332" }}>
                <div style={{ color:"#60a5fa" }}>
                  [{formatBrisbaneTime(l.started_at)} AEST]{" "}
                  <span style={{ color:"#f87171", fontWeight:"bold" }}>
                    {l.mode?.toUpperCase()}
                  </span>
                  {" :: "}
                  <span style={{ color:"#fbbf24" }}>{l.source}</span>
                </div>
                <div>
                  status=<span style={{ color: l.status==="ok"?"#4ade80":l.status==="error"?"#f87171":"#fbbf24" }}>
                    {l.status}
                  </span>
                  {" "}records={l.records_total} ok={l.records_ok} failed={l.records_failed}
                </div>
                {l.message && <div style={{ color:"#94a3b8" }}># {l.message}</div>}
                {l.warnings?.slice(0, 5).map((w, i) => (
                  <div key={i} style={{ color:"#fbbf24" }}>! {w}</div>
                ))}
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
}